import { reactive } from 'vue';
import VisitorInfoAPI from 'dashboard/api/visitorInfo';
import {
  isRentVisitorContext,
  normalizeVisitorBusinessContext,
} from '../utils/visitorInfoPresenter';

const payloadFrom = response => response?.data?.payload || response?.payload;
const metaFrom = response => response?.data?.meta || response?.meta || {};
const DEFAULT_HISTORY_META = Object.freeze({
  page: 0,
  perPage: 6,
  nextPage: null,
  hasMore: false,
});

const requestErrorKind = (error, fallback) => {
  const status = Number(
    error?.response?.status || error?.status || error?.request?.status
  );
  return status === 403 ? 'permission' : fallback;
};

const visitRecordKey = item =>
  item?.id || item?.visit_id || item?.visitCode || item?.visit_code;

export const useVisitorInfo = () => {
  const domainData = reactive({
    visitorId: '',
    businessContext: 'for_rent',
    visitorInfo: null,
    suggestedHouses: [],
    favoriteHouses: [],
    visitedHouses: [],
    nextVisitsWithMe: [],
    lastVisits: [],
    visitHistory: [],
    historyMeta: { ...DEFAULT_HISTORY_META },
    ongoingOffers: [],
  });

  const requestsState = reactive({
    loadingVisitorInfo: false,
    loadingSuggestedHouses: false,
    loadingHouses: false,
    loadingNextVisitsWithMe: false,
    loadingLastVisits: false,
    loadingVisitHistory: false,
    loadingMoreVisitHistory: false,
    loadingOngoingOffers: false,
  });

  const errors = reactive({
    visitorInfo: '',
    suggestedHouses: '',
    houses: '',
    nextVisitsWithMe: '',
    lastVisits: '',
    visitHistory: '',
    ongoingOffers: '',
  });

  let requestSerial = 0;

  const reset = ({ invalidate = false } = {}) => {
    if (invalidate) requestSerial += 1;

    domainData.visitorInfo = null;
    domainData.suggestedHouses = [];
    domainData.favoriteHouses = [];
    domainData.visitedHouses = [];
    domainData.nextVisitsWithMe = [];
    domainData.lastVisits = [];
    domainData.visitHistory = [];
    domainData.historyMeta = { ...DEFAULT_HISTORY_META };
    domainData.ongoingOffers = [];
    domainData.visitorId = '';
    domainData.businessContext = 'for_rent';
    errors.visitorInfo = '';
    errors.suggestedHouses = '';
    errors.houses = '';
    errors.nextVisitsWithMe = '';
    errors.lastVisits = '';
    errors.visitHistory = '';
    errors.ongoingOffers = '';
    Object.keys(requestsState).forEach(key => {
      requestsState[key] = false;
    });
  };

  const applyIfCurrent = (serial, callback) => {
    if (serial !== requestSerial) return;
    callback();
  };

  const loadVisitorInfo = async (serial, visitorId, businessContext) => {
    requestsState.loadingVisitorInfo = true;
    errors.visitorInfo = '';
    try {
      const response = await VisitorInfoAPI.show(visitorId, {
        business_context: businessContext,
      });
      applyIfCurrent(serial, () => {
        domainData.visitorInfo = payloadFrom(response) || null;
      });
    } catch (error) {
      applyIfCurrent(serial, () => {
        errors.visitorInfo = requestErrorKind(error, 'visitor_info');
      });
    } finally {
      applyIfCurrent(serial, () => {
        requestsState.loadingVisitorInfo = false;
      });
    }
  };

  const loadSuggestedHouses = async (serial, visitorId, businessContext) => {
    if (!isRentVisitorContext(businessContext)) {
      domainData.suggestedHouses = [];
      return;
    }

    requestsState.loadingSuggestedHouses = true;
    errors.suggestedHouses = '';
    try {
      const response = await VisitorInfoAPI.suggestions(visitorId, {
        business_context: businessContext,
      });
      applyIfCurrent(serial, () => {
        domainData.suggestedHouses = payloadFrom(response) || [];
      });
    } catch (error) {
      applyIfCurrent(serial, () => {
        errors.suggestedHouses = requestErrorKind(error, 'suggested_houses');
        domainData.suggestedHouses = [];
      });
    } finally {
      applyIfCurrent(serial, () => {
        requestsState.loadingSuggestedHouses = false;
      });
    }
  };

  const loadHouses = async (serial, visitorId, businessContext) => {
    requestsState.loadingHouses = true;
    errors.houses = '';
    try {
      const response = await VisitorInfoAPI.houses(visitorId, {
        business_context: businessContext,
      });
      const payload = payloadFrom(response) || {};
      applyIfCurrent(serial, () => {
        domainData.favoriteHouses =
          payload.favorite_houses || payload.favoriteHouses || [];
        domainData.visitedHouses =
          payload.visited_houses || payload.visitedHouses || [];
      });
    } catch (error) {
      applyIfCurrent(serial, () => {
        errors.houses = requestErrorKind(error, 'houses');
        domainData.favoriteHouses = [];
        domainData.visitedHouses = [];
      });
    } finally {
      applyIfCurrent(serial, () => {
        requestsState.loadingHouses = false;
      });
    }
  };

  const loadNextVisits = async serial => {
    requestsState.loadingNextVisitsWithMe = true;
    errors.nextVisitsWithMe = '';
    try {
      const response = await VisitorInfoAPI.nextVisits(domainData.visitorId);
      applyIfCurrent(serial, () => {
        domainData.nextVisitsWithMe = payloadFrom(response) || [];
      });
    } catch (error) {
      applyIfCurrent(serial, () => {
        errors.nextVisitsWithMe = requestErrorKind(error, 'next_visits');
        domainData.nextVisitsWithMe = [];
      });
    } finally {
      applyIfCurrent(serial, () => {
        requestsState.loadingNextVisitsWithMe = false;
      });
    }
  };

  const loadLastVisits = async serial => {
    requestsState.loadingLastVisits = true;
    errors.lastVisits = '';
    try {
      const response = await VisitorInfoAPI.lastVisits(domainData.visitorId);
      applyIfCurrent(serial, () => {
        domainData.lastVisits = payloadFrom(response) || [];
      });
    } catch (error) {
      applyIfCurrent(serial, () => {
        errors.lastVisits = requestErrorKind(error, 'last_visits');
        domainData.lastVisits = [];
      });
    } finally {
      applyIfCurrent(serial, () => {
        requestsState.loadingLastVisits = false;
      });
    }
  };

  const applyHistoryPayload = ({ payload, meta, page, append }) => {
    const items = Array.isArray(payload) ? payload : [];
    const nextItems = append
      ? [
          ...domainData.visitHistory,
          ...items.filter(item => {
            const key = visitRecordKey(item);
            if (!key) return true;
            return !domainData.visitHistory.some(
              existing => visitRecordKey(existing) === key
            );
          }),
        ]
      : items;

    domainData.visitHistory = nextItems;
    domainData.historyMeta = {
      page: Number(meta.page || page || 1),
      perPage: Number(
        meta.per_page || meta.perPage || DEFAULT_HISTORY_META.perPage
      ),
      nextPage: meta.next_page || meta.nextPage || null,
      hasMore: Boolean(meta.has_more ?? meta.hasMore),
    };
  };

  const loadVisitHistory = async (
    serial,
    { page = 1, append = false } = {}
  ) => {
    const loadingKey = append
      ? 'loadingMoreVisitHistory'
      : 'loadingVisitHistory';
    requestsState[loadingKey] = true;
    errors.visitHistory = '';
    try {
      const response = await VisitorInfoAPI.history(domainData.visitorId, {
        page,
        per_page: DEFAULT_HISTORY_META.perPage,
      });
      applyIfCurrent(serial, () => {
        applyHistoryPayload({
          payload: payloadFrom(response),
          meta: metaFrom(response),
          page,
          append,
        });
      });
    } catch (error) {
      applyIfCurrent(serial, () => {
        errors.visitHistory = requestErrorKind(error, 'visit_history');
        if (!append) {
          domainData.visitHistory = [];
          domainData.historyMeta = { ...DEFAULT_HISTORY_META };
        }
      });
    } finally {
      applyIfCurrent(serial, () => {
        requestsState[loadingKey] = false;
      });
    }
  };

  const loadMoreVisitHistory = async () => {
    const nextPage = domainData.historyMeta.nextPage;
    if (
      !domainData.visitorId ||
      !nextPage ||
      requestsState.loadingVisitHistory ||
      requestsState.loadingMoreVisitHistory
    )
      return;

    await loadVisitHistory(requestSerial, { page: nextPage, append: true });
  };

  const loadOngoingOffers = async (serial, businessContext) => {
    requestsState.loadingOngoingOffers = true;
    errors.ongoingOffers = '';
    try {
      const response = await VisitorInfoAPI.ongoingOffers(
        domainData.visitorId,
        {
          business_context: businessContext,
        }
      );
      applyIfCurrent(serial, () => {
        domainData.ongoingOffers = payloadFrom(response) || [];
      });
    } catch (error) {
      applyIfCurrent(serial, () => {
        errors.ongoingOffers = requestErrorKind(error, 'ongoing_offers');
        domainData.ongoingOffers = [];
      });
    } finally {
      applyIfCurrent(serial, () => {
        requestsState.loadingOngoingOffers = false;
      });
    }
  };

  const load = async (visitorId, businessContext) => {
    requestSerial += 1;
    const serial = requestSerial;
    const normalizedContext = normalizeVisitorBusinessContext(businessContext);
    reset();
    domainData.visitorId = visitorId;
    domainData.businessContext = normalizedContext;

    if (!visitorId) return;

    await Promise.allSettled([
      loadVisitorInfo(serial, visitorId, normalizedContext),
      loadSuggestedHouses(serial, visitorId, normalizedContext),
      loadHouses(serial, visitorId, normalizedContext),
      loadNextVisits(serial),
      loadLastVisits(serial),
      loadVisitHistory(serial),
      loadOngoingOffers(serial, normalizedContext),
    ]);
  };

  return {
    domainData,
    requestsState,
    errors,
    load,
    loadMoreVisitHistory,
    reset,
  };
};

export default useVisitorInfo;
